import ArrowLeft from "@/assets/icons/ArrowLeft";
import ArrowRight from "@/assets/icons/ArrowRight";
import ProtectedRoute from "@/components/layout/ProtectedRoute";
import Screen from "@/components/layout/Screen";
import AppButton from "@/components/shared/AppButton";
import { LicensePlateInput } from "@/components/shared/LicensePlatePicker/LicensePlateInput";
import { LicensePlateRadio } from "@/components/shared/LicensePlatePicker/LicensePlateRadio";
import PageTitle from "@/components/shared/PageTitle/PageTitle";
import PlateModal from "@/components/shared/PlateModal";
import PlateWithWarningModal from "@/components/shared/PlateWithWarningModal";
import SessionInProgressModal from "@/components/shared/SessionInProgressModal";
import TermsTextLink from "@/components/shared/TermsTextLink";
import { HStack } from "@/components/ui/hstack";
import { Skeleton, SkeletonText } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { useSelectPlate } from "@/hooks/useSelectPlate";
import { fetchPlate } from "@/services/plate.queries";
import { useToastNotify } from "@/stores/toastStore";
import { validateLicensePlate } from "@/utils/licensePlateValidation";
import { saveToStorage } from "@/utils/secureStore";
import * as Sentry from "@sentry/react-native";
import { useT } from "@transifex/react";
import { router, useLocalSearchParams } from "expo-router";
import type { AlreadyPaidData, WarningData } from "ipctg-inflow-types/InFlowAPIMisc";
import { useCallback, useEffect, useState } from "react";
import { Platform } from "react-native";

export default function RedeemFlexPark() {
  const { passID } = useLocalSearchParams();
  const { notify } = useToastNotify();
  const t = useT();

  const {
    inputValue,
    setInputValue,
    setLicensePlate,
    selectedPlate,
    setSelectedPlate,
    isRemoveModalOpen,
    setIsRemoveModalOpen,
    plateToRemove,
    isRemoving,
    errors,
    setErrors,
    handleDeletePlate,
    confirmRemovePlate,
    handleLicensePlateChange,
    clearLicensePlateError,
    account,
    isAccountFetching,
  } = useSelectPlate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [plateInputError, setPlateInputError] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isPlateWarningDataModalOpen, setIsPlateWarningDataModalOpen] = useState(false);
  const [plateWarningData, setPlateWarningData] = useState<WarningData | null>(null);

  const [isSessionInProgressModalOpen, setIsSessionInProgressModalOpen] = useState(false);
  const [alreadyPaidData, setAlreadyPaidData] = useState<AlreadyPaidData | null>(null);

  const resetState = useCallback(() => {
    setInputValue("");
    setLicensePlate("");
    setSelectedPlate("");
  }, [setInputValue, setLicensePlate, setSelectedPlate]);

  useEffect(() => {
    resetState();
  }, [resetState]);

  const handleSubmit = async () => {
    const newErrors: {
      radio: string | null;
      licensePlate: string | null;
    } = {
      radio: null,
      licensePlate: null,
    };

    if (account?.savedPlates && account.savedPlates.length > 0 && !selectedPlate) {
      newErrors.radio = t("Please select an option.");
    }

    const plateError = validateLicensePlate(inputValue);
    if (plateError) {
      newErrors.licensePlate = plateError;
      setPlateInputError(true);
    } else {
      setPlateInputError(false);
    }

    setErrors(newErrors);

    if (Object.values(newErrors).every((error) => error === null)) {
      setIsLoading(true);
      try {
        const response = await fetchPlate({ plate: inputValue });
        if (response.alreadyPaid) {
          setAlreadyPaidData(response.alreadyPaidData);
          setIsSessionInProgressModalOpen(true);
        } else if (response.warning && response.warningData) {
          setIsPlateWarningDataModalOpen(true);
          setPlateWarningData(response.warningData);
        } else if (!response.plateInConvenience) {
          setIsModalOpen(true);
        } else {
          router.push({
            pathname: "/redeem/confirmation",
            params: { passID: passID, termsAndConditionsOKTimestamp: new Date().toISOString() },
          });
        }
      } catch (error) {
        console.error("Error fetching plate information:", error);
        Sentry.captureException(error, {
          extra: {
            data: {
              plate: inputValue,
            },
            localStorage: Platform.OS === "web" ? window.localStorage : null,
          },
        });
        notify({ severity: "error", message: t("An error occurred while processing your request.") });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSelectPlate = (value: string) => {
    const formattedPlate = value.trim().toLocaleUpperCase();
    setSelectedPlate(formattedPlate);
    if (formattedPlate !== "NEW") {
      setInputValue(formattedPlate);
      setLicensePlate(formattedPlate);
      setPlateInputError(false);
    } else {
      setInputValue("");
      setLicensePlate("");
      setPlateInputError(true);
    }
    setErrors((prevErrors) => ({ ...prevErrors, radio: null }));
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await saveToStorage("licensePlate", inputValue);
      setIsLoading(false);
      setIsModalOpen(false);
      setIsPlateWarningDataModalOpen(false);

      router.push({
        pathname: "/redeem/confirmation",
        params: { passID: passID, termsAndConditionsOKTimestamp: new Date().toISOString() },
      });
    } catch (error) {
      console.error("Error saving data:", error);
      Sentry.captureException(error, {
        extra: {
          localStorage: Platform.OS === "web" ? window.localStorage : null,
        },
      });
      notify({ severity: "error", message: t("An error occurred while processing your request.") });
    }
  };

  // const handleConfirm = async () => {
  //   setIsLoading(true);
  //   try {
  //     const response = await fetchPlate({ plate: inputValue });

  //     if (response.alreadyPaid) {
  //       setAlreadyPaidData(response.alreadyPaidData);
  //       setIsSessionInProgressModalOpen(true);
  //     } else if (response.warning && response.warningData) {
  //       setIsPlateWarningDataModalOpen(true);
  //       setPlateWarningData(response.warningData);
  //     } else if (!response.plateInConvenience) {
  //       setIsModalOpen(true);
  //     } else {
  //       await saveToStorage("licensePlate", inputValue);
  //       router.push({
  //         pathname: "/redeem/confirmation",
  //         params: { passID: passID, termsAndConditionsOKTimestamp: new Date().toISOString() },
  //       });
  //     }
  //   } catch (error) {
  //     console.error("Error fetching plate information:", error);
  //     Sentry.captureException(error);
  //     notify({ severity: "error", message: t("An error occurred while processing your request.") });
  //   } finally {
  //     setIsLoading(false);
  //     setIsModalOpen(false);
  //   }
  // };

  // const hasSavedPlates = account?.savedPlates && account?.savedPlates?.length > 0;
  const hasSavedPlates = !!(account?.savedPlates && account.savedPlates.length > 0);

  if (isAccountFetching) {
    return <RedeemFlexParkScreenSkeleton hasSavedPlates={hasSavedPlates} />;
  }

  return (
    <ProtectedRoute>
      <Screen>
        <PageTitle title={t("Which vehicle are you parking?")} />

        {hasSavedPlates ? (
          <VStack className="gap-[20px]">
            <LicensePlateRadio
              selectedValue={selectedPlate}
              onValueChange={handleSelectPlate}
              plates={account.savedPlates.map((plate) => ({
                id: plate.plate,
                plate: plate.plate,
              }))}
              onDelete={handleDeletePlate}
            />

            {errors.radio && <Text className="text-error-100 text-xs font-semibold">{errors.radio}</Text>}

            {selectedPlate === "NEW" && (
              <LicensePlateInput
                onAdd={handleLicensePlateChange}
                setInputValue={setInputValue}
                inputValue={inputValue}
                setInputError={setPlateInputError}
                inputError={plateInputError}
                errorMessage={errors.licensePlate}
                clearError={clearLicensePlateError}
              />
            )}
          </VStack>
        ) : (
          // SHOW INPUT IF THERE ISNT SAVED PLATES
          <LicensePlateInput
            onAdd={handleLicensePlateChange}
            setInputValue={setInputValue}
            inputValue={inputValue}
            setInputError={setPlateInputError}
            inputError={plateInputError}
            errorMessage={errors.licensePlate}
            clearError={clearLicensePlateError}
          />
        )}

        <TermsTextLink />

        <HStack className="gap-[12px]">
          <AppButton
            icon={ArrowLeft}
            onPress={() => {
              resetState();
              if (!router.canGoBack()) {
                router.replace("/welcome");
              } else {
                router.back();
              }
            }}
            title={t("Back")}
            variant="outline"
          />
          <AppButton
            className="flex-1"
            icon={ArrowRight}
            iconPosition="after"
            loading={isLoading}
            onPress={handleSubmit}
            title={t("Agree and Continue")}
            variant="primary"
          />
        </HStack>

        <PlateModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} licensePlate={inputValue} onConfirm={handleConfirm} type={"confirm"} />

        <PlateModal
          isOpen={isRemoveModalOpen}
          type="remove"
          licensePlate={plateToRemove}
          onClose={() => setIsRemoveModalOpen(false)}
          onConfirm={confirmRemovePlate}
          isRemoving={isRemoving}
        />

        <SessionInProgressModal
          isOpen={isSessionInProgressModalOpen}
          onClose={() => setIsSessionInProgressModalOpen(false)}
          alreadyPaidData={alreadyPaidData}
          plate={inputValue}
        />

        <PlateWithWarningModal
          isOpen={isPlateWarningDataModalOpen}
          onClose={() => setIsPlateWarningDataModalOpen(false)}
          cameraLotName={plateWarningData?.cameraLotName ?? ""}
          qrLotName={plateWarningData?.qrLotName ?? ""}
          onConfirm={handleConfirm}
        />
      </Screen>
    </ProtectedRoute>
  );
}

function RedeemFlexParkScreenSkeleton({ hasSavedPlates }: { hasSavedPlates: boolean }) {
  return (
    <Screen>
      <VStack className="gap-[32px]">
        <VStack className="gap-[20px]">
          <SkeletonText className="h-[20px] w-[85%] self-center" />
          {hasSavedPlates ? (
            <VStack className="gap-[12px]">
              <Skeleton className="h-[64px] w-full rounded-md" />
              <Skeleton className="h-[64px] w-full rounded-md" />
            </VStack>
          ) : (
            <Skeleton className="h-[64px] w-full rounded-md" />
          )}
        </VStack>
        <SkeletonText _lines={2} className="h-[14px] w-[80%] self-center" />
        <HStack className="gap-[12px] mt-6">
          <Skeleton className="w-[30%] h-[52px] rounded-md" />
          <Skeleton className="flex-1 h-[52px] rounded-md" />
        </HStack>
      </VStack>
    </Screen>
  );
}
